"use client";

import React, {useEffect, useRef, useState} from "react";
import {Container} from "@/shared/components/shared";
import Script from "next/script";
import {Button} from "@/shared/components/ui/button";
import Image from "next/image";

declare global {
    interface Window {
        ymaps: any;
    }
}

export const MapContacts: React.FC = () => {
    const [mapState, setMapState] = useState<'idle' | 'loading' | 'loaded' | 'error' | 'stalled'>('idle');
    const [reloadKey, setReloadKey] = useState(0); // Ключ для принудительной перезагрузки
    const mapContainerRef = useRef<HTMLDivElement | null>(null);
    const mapInstanceRef = useRef<any>(null);
    const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const scriptLoadedRef = useRef(false); // Флаг загрузки скрипта

    const yandexKey =
        process.env.NEXT_PUBLIC_YANDEX_API_KEY ||
        '5b2a9a4d-b5ee-425a-9f9d-34dd0477bcc2';

    const initMap = () => {
        if (!window.ymaps || !mapContainerRef.current) {
            console.log('Cannot init map - ymaps or container not ready');
            return;
        }

        console.log('Starting map initialization...');
        setMapState('loading');

        // Таймаут для определения зависания (10 секунд)
        if (loadingTimeoutRef.current) {
            clearTimeout(loadingTimeoutRef.current);
        }

        loadingTimeoutRef.current = setTimeout(() => {
            if (mapState === 'loading') {
                console.warn('Map loading timeout');
                setMapState('stalled');
            }
        }, 10000);

        window.ymaps.ready(() => {
            try {
                // Уничтожаем старую карту если есть
                if (mapInstanceRef.current) {
                    try {
                        mapInstanceRef.current.destroy();
                    } catch (e) {
                        console.log('Error destroying old map:', e);
                    }
                    mapInstanceRef.current = null;
                }

                const map = new window.ymaps.Map(mapContainerRef.current, {
                    center: [43.117964, 131.93111],
                    zoom: 16,
                    controls: ['zoomControl', 'fullscreenControl'],
                });

                map.behaviors.disable("scrollZoom");

                const marker = new window.ymaps.Placemark(
                    [43.117964, 131.93111],
                    {
                        hintContent: 'АвтоГарантСити',
                        balloonContent: 'Россия, Владивосток, ул. Шилкинская, 32а',
                    },
                    {
                        preset: 'islands#orangeIcon',
                    }
                );

                map.geoObjects.add(marker);
                mapInstanceRef.current = map;

                // Очищаем таймаут
                if (loadingTimeoutRef.current) {
                    clearTimeout(loadingTimeoutRef.current);
                    loadingTimeoutRef.current = null;
                }

                setMapState('loaded');
                console.log('Map initialized successfully');
            } catch (error) {
                console.error('Error creating map:', error);
                if (loadingTimeoutRef.current) {
                    clearTimeout(loadingTimeoutRef.current);
                    loadingTimeoutRef.current = null;
                }
                setMapState('error');
            }
        });
    };

    const handleScriptLoad = () => {
        console.log('Yandex Maps script loaded');
        scriptLoadedRef.current = true;
        // Инициализируем карту сразу после загрузки скрипта
        setTimeout(() => {
            initMap();
        }, 100);
    };

    const handleScriptError = () => {
        console.error('Failed to load Yandex Maps script');
        setMapState('error');
    };

    // Эффект для перезагрузки карты при изменении reloadKey
    useEffect(() => {
        if (reloadKey > 0 && scriptLoadedRef.current) {
            console.log('Reload triggered, key:', reloadKey);
            setTimeout(() => {
                initMap();
            }, 100);
        }
    }, [reloadKey]);

    // Cleanup при размонтировании
    useEffect(() => {
        return () => {
            if (loadingTimeoutRef.current) {
                clearTimeout(loadingTimeoutRef.current);
            }
            if (mapInstanceRef.current) {
                try {
                    mapInstanceRef.current.destroy();
                } catch (e) {
                    console.log('Cleanup error:', e);
                }
            }
        };
    }, []);

    const handleForceReload = () => {
        console.log('Force reload');

        // Очищаем таймаут
        if (loadingTimeoutRef.current) {
            clearTimeout(loadingTimeoutRef.current);
            loadingTimeoutRef.current = null;
        }

        // Уничтожаем карту
        if (mapInstanceRef.current) {
            try {
                mapInstanceRef.current.destroy();
            } catch (e) {
                console.log('Destroy error:', e);
            }
            mapInstanceRef.current = null;
        }

        // Триггерим перезагрузку через изменение ключа
        setReloadKey(prev => prev + 1);
    };

    return (
        <section className={'pb-8 lg:pb-18'} id={'contacts'}>
            <Script
                src={`https://api-maps.yandex.ru/2.1/?apikey=${yandexKey}&lang=ru_RU`}
                strategy="afterInteractive"
                onLoad={handleScriptLoad}
                onError={handleScriptError}
            />

            <Container className={'pt-15 lg:pt-29 pb-0'}>
                <h2 className={'text-custom-orange font-bold text-[30px] lg:text-[70px] leading-[1.2] uppercase w-full text-center'}>
                    Мы рады вам 7 дней в неделю
                </h2>
            </Container>

            <Container className={'pt-10 lg:pt-18 pb-0 lg:pb-13'}>
                <div className={'flex flex-col lg:flex-row gap-8 lg:gap-12'}>
                    {/* Карта */}
                    <div className={'flex-1 lg:w-1/2'}>
                        <div
                            ref={mapContainerRef}
                            className="w-full rounded-lg overflow-hidden relative h-[400px] lg:h-[600px] bg-gray-100"
                        >
                            {/* Постоянная кнопка перезагрузки в углу */}
                            <button
                                onClick={handleForceReload}
                                className="absolute top-4 right-4 z-[35] bg-white/90 hover:bg-white shadow-lg rounded-full p-3 transition-all group"
                                title="Перезагрузить карту"
                            >
                                <svg
                                    className="w-5 h-5 text-custom-orange group-hover:rotate-180 transition-transform duration-500"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                                </svg>
                            </button>

                            <Button
                                onClick={() => {
                                    const [lat, lon] = [43.117964, 131.931110];
                                    const url = `https://yandex.ru/maps/?rtext=~${lat},${lon}&rtt=auto`;
                                    window.open(url, '_blank');
                                }}
                                className="absolute left-1/2 -translate-x-1/2 flex items-center gap-3 hover:opacity-90 z-20 bottom-[40px] py-3 lg:py-5 px-6 lg:px-12 text-[14px] lg:text-[16px]"
                            >
                                        <span className="inline-block" aria-hidden>
                                          <svg
                                              width="15"
                                              height="22"
                                              viewBox="0 0 15 22"
                                              fill="none"
                                              xmlns="http://www.w3.org/2000/svg"
                                          >
                                            <path
                                                d="M0.0475068 4.37611C-0.0284181 4.22426 -0.0121365 4.04534 0.0909014 3.91519L2.94325 0.173511C3.02459 0.0650574 3.15475 0 3.29031 0C3.42588 0 3.55603 0.0650574 3.64279 0.173511L6.49514 3.91519C6.59818 4.04534 6.60901 4.22972 6.53853 4.37611C6.46261 4.52796 6.3108 4.62013 6.14266 4.62013H5.4106V8.45402C3.55603 9.37046 2.05935 10.8943 1.17544 12.7651V4.62013H0.443376C0.27528 4.62013 0.123431 4.52251 0.0475068 4.37611Z"
                                                fill="white"
                                            />
                                            <path
                                                d="M14.1145 10.8565L10.3728 13.7088C10.2427 13.8119 10.0583 13.8281 9.91189 13.7522C9.76004 13.6763 9.66787 13.5245 9.66787 13.3564V12.6243H9.14185C7.0812 12.6243 5.41101 14.2999 5.41101 16.3551V20.5795C5.41125 20.6372 5.40005 20.6944 5.37807 20.7478C5.35608 20.8012 5.32373 20.8497 5.2829 20.8906C5.24207 20.9314 5.19355 20.9637 5.14015 20.9857C5.08675 21.0077 5.02953 21.0189 4.97178 21.0187H1.6151C1.55736 21.0189 1.50013 21.0077 1.44673 20.9857C1.39333 20.9637 1.34482 20.9314 1.30398 20.8906C1.26315 20.8497 1.2308 20.8012 1.20882 20.7478C1.18683 20.6944 1.17563 20.6372 1.17588 20.5795V16.6751C1.17588 12.1091 4.89044 8.39459 9.45638 8.39459H9.67329V7.66253C9.67329 7.49443 9.76546 7.34258 9.91731 7.26666C10.0692 7.19615 10.2481 7.20702 10.3782 7.31005L14.1199 10.1624C14.2284 10.2437 14.2935 10.3739 14.2935 10.5148C14.288 10.6504 14.2284 10.7751 14.1145 10.8565Z"
                                                fill="white"
                                            />
                                          </svg>
                                        </span>
                                <span className="text-white font-button font-bold leading-[1.2]">
                                          Проложить маршрут
                                        </span>
                            </Button>
                            {(mapState === 'loading' || mapState === 'idle') && (
                                <div
                                    className="absolute inset-0 flex items-center justify-center bg-gray-100/95 backdrop-blur-sm z-[25]">
                                    <div className="text-custom-black text-center px-6">
                                        <div
                                            className="animate-spin rounded-full h-10 w-10 border-b-2 border-custom-orange mx-auto mb-4"></div>
                                        <p className="mb-4 text-base font-medium">Загрузка карты...</p>
                                        <button
                                            onClick={handleForceReload}
                                            className="px-5 py-2.5 bg-custom-orange text-white rounded-md hover:bg-orange-600 transition-colors text-sm font-semibold shadow-md"
                                        >
                                            Загрузить заново
                                        </button>
                                    </div>
                                </div>
                            )}
                            {mapState === 'stalled' && (
                                <div
                                    className="absolute inset-0 flex items-center justify-center bg-gray-100/95 backdrop-blur-sm z-[25]">
                                    <div className="text-center px-6">
                                        <div className="mb-4">
                                            <svg className="w-16 h-16 text-orange-500 mx-auto" fill="none"
                                                 stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                            </svg>
                                        </div>
                                        <p className="text-orange-500 font-bold mb-2 text-lg">
                                            Карта загружается слишком долго
                                        </p>
                                        <p className="text-gray-600 mb-5 text-sm">
                                            Попробуйте загрузить заново
                                        </p>
                                        <button
                                            onClick={handleForceReload}
                                            className="px-7 py-3 bg-custom-orange text-white rounded-md hover:bg-orange-600 transition-colors font-bold shadow-lg"
                                        >
                                            Загрузить заново
                                        </button>
                                    </div>
                                </div>
                            )}
                            {mapState === 'error' && (
                                <div
                                    className="absolute inset-0 flex items-center justify-center bg-gray-100/95 backdrop-blur-sm z-[25]">
                                    <div className="text-center px-6">
                                        <div className="mb-4">
                                            <svg className="w-16 h-16 text-red-500 mx-auto" fill="none"
                                                 stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                      d="M6 18L18 6M6 6l12 12"/>
                                            </svg>
                                        </div>
                                        <p className="text-red-500 font-bold mb-2 text-lg">
                                            Не удалось загрузить карту
                                        </p>
                                        <p className="text-gray-600 mb-5 text-sm">
                                            Попробуйте загрузить ещё раз
                                        </p>
                                        <button
                                            onClick={handleForceReload}
                                            className="px-7 py-3 bg-custom-orange text-white rounded-md hover:bg-orange-600 transition-colors font-bold shadow-lg"
                                        >
                                            Загрузить заново
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Контактная информация */}
                    <div className={'flex-1 lg:w-1/2 flex flex-col'}>
                        <div className={'bg-gray-50 rounded-lg p-6 lg:p-10 h-full flex flex-col'}>
                            <h3 className={'text-custom-orange font-bold text-[24px] lg:text-[42px] uppercase leading-[1.2] mb-6 lg:mb-10 text-center'}>
                                Контакты
                            </h3>

                            <div className={'flex-1 flex flex-col justify-center items-center'}>
                                <div className={'space-y-6 lg:space-y-8'}>
                                    {/* Адрес */}
                                    <div className={'flex items-center gap-4 lg:gap-6'}>
                                        <svg
                                            width="40"
                                            height="40"
                                            viewBox="0 0 46 67"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                            className={'w-[30px] h-[30px] lg:w-[40px] lg:h-[40px] flex-shrink-0'}
                                        >
                                            <path
                                                d="M23 0C10.2975 0 0 10.2975 0 23C0 39.25 23 67 23 67C23 67 46 39.25 46 23C46 10.2975 35.7025 0 23 0ZM23 31.1667C18.4917 31.1667 14.8333 27.5083 14.8333 23C14.8333 18.4917 18.4917 14.8333 23 14.8333C27.5083 14.8333 31.1667 18.4917 31.1667 23C31.1667 27.5083 27.5083 31.1667 23 31.1667Z"
                                                fill="#FF6B00"/>
                                        </svg>
                                        <div>
                                            <p className={'text-custom-black text-[16px] lg:text-[24px] leading-[1.3]'}>
                                                г. Владивосток
                                            </p>
                                            <p className={'text-custom-black font-bold text-[18px] lg:text-[28px] leading-[1.3]'}>
                                                ул. Шилкинская 32а
                                            </p>
                                        </div>
                                    </div>

                                    {/* Время работы */}
                                    <div className={'flex items-center gap-4 lg:gap-6'}>
                                        <svg
                                            width="40"
                                            height="40"
                                            viewBox="0 0 40 40"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                            className={'w-[30px] h-[30px] lg:w-[40px] lg:h-[40px] flex-shrink-0'}
                                        >
                                            <circle cx="20" cy="20" r="18" stroke="#FF6B00" strokeWidth="2"
                                                    fill="none"/>
                                            <path d="M20 8V20L28 24" stroke="#FF6B00" strokeWidth="2"
                                                  strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                        <p className={'text-custom-black text-[18px] lg:text-[28px] leading-[1.3] font-bold'}>
                                            09:00 - 20:00
                                        </p>
                                    </div>

                                    {/* Телефон */}
                                    <div className={'flex items-center gap-4 lg:gap-6'}>
                                        <svg
                                            width="40"
                                            height="40"
                                            viewBox="0 0 40 40"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                            className={'w-[30px] h-[30px] lg:w-[40px] lg:h-[40px] flex-shrink-0'}
                                        >
                                            <path
                                                d="M36.6667 28.2V33.2C36.6683 33.6493 36.5727 34.0939 36.3868 34.5042C36.201 34.9145 35.9294 35.2808 35.5902 35.5785C35.251 35.8762 34.8521 36.0986 34.4209 36.2311C33.9896 36.3637 33.536 36.4033 33.0889 36.3472C27.9485 35.7442 23.0447 33.9262 18.7556 31.0472C14.7988 28.4494 11.4394 25.0899 8.84167 21.1333C5.95556 16.8311 4.13714 11.9118 3.54167 6.75721C3.48571 6.31149 3.52482 5.85932 3.65648 5.42939C3.78814 4.99946 4.00928 4.60148 4.30518 4.26266C4.60108 3.92383 4.96548 3.65194 5.37406 3.46525C5.78265 3.27855 6.22552 3.18163 6.67333 3.18277H11.6733C12.4822 3.17446 13.2647 3.46062 13.8796 3.98925C14.4944 4.51788 14.8999 5.25139 15.0178 6.05166C15.2377 7.65139 15.6469 9.22099 16.2367 10.7294C16.4549 11.3002 16.5028 11.9233 16.3742 12.5214C16.2456 13.1195 15.9463 13.6675 15.5122 14.1028L13.4333 16.1817C15.8602 20.3113 19.5353 23.9864 23.665 26.4133L25.7439 24.3344C26.1792 23.9003 26.7272 23.601 27.3253 23.4724C27.9234 23.3438 28.5465 23.3917 29.1172 23.6099C30.6257 24.1997 32.1953 24.6089 33.795 24.8289C34.6027 24.9476 35.3418 25.3588 35.8719 25.9815C36.402 26.6042 36.6854 27.3966 36.6667 28.2128V28.2Z"
                                                fill="#FF6B00"/>
                                        </svg>
                                        <a
                                            href={'tel:+74232222999'}
                                            className={'text-custom-black text-[18px] lg:text-[28px] leading-[1.3] font-bold hover:text-custom-orange transition-colors'}
                                        >
                                            +7 (423) 2222-999
                                        </a>
                                    </div>

                                    {/* WhatsApp */}
                                    <div className={'flex items-center gap-4 lg:gap-6'}>
                                        <Image
                                            src={'/images/whatsapp.svg'}
                                            alt={'whatsapp'}
                                            width={40}
                                            height={40}
                                            className={'w-[30px] h-[30px] lg:w-[40px] lg:h-[40px] flex-shrink-0'}
                                        />
                                        <a
                                            href={'https://wa.me/74232222999'}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={'text-custom-black text-[18px] lg:text-[28px] leading-[1.3] font-bold hover:text-custom-orange transition-colors'}
                                        >
                                            +7 (423) 2222-999
                                        </a>
                                    </div>
                                    <div className={'flex items-center gap-4 lg:gap-6'}>
                                        <Image
                                            src={'/images/telegram-icon.svg'}
                                            alt={'telegram'}
                                            width={40}
                                            height={40}
                                            className={'w-[30px] h-[30px] lg:w-[40px] lg:h-[40px] flex-shrink-0'}
                                        />
                                        <a
                                            href={'https://t.me/Autogarantcity'}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={'text-custom-black text-[18px] lg:text-[28px] leading-[1.3] font-bold hover:text-custom-orange transition-colors'}
                                        >
                                            @Autogarantcity
                                        </a>
                                    </div>
                                    <div className={'flex items-center gap-4 lg:gap-6'}>
                                        <Image
                                            src={'/images/max-logo.png'}
                                            alt={'max'}
                                            width={40}
                                            height={40}
                                            className={'w-[30px] h-[30px] lg:w-[40px] lg:h-[40px] flex-shrink-0'}
                                        />
                                        <a
                                            href={'https://max.ru/u/f9LHodD0cOKrK7neSPMdva_YCBHMvrWh_-rSLnmo4epBf3ovOxvF2p08WGc'}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={'text-custom-black text-[18px] lg:text-[28px] leading-[1.3] font-bold hover:text-custom-orange transition-colors'}
                                        >
                                            АвтоГарантСити
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Container>
        </section>
    );
};
